import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Home, Star, Leaf, Zap, DollarSign, Heart, TrendingUp,
  CheckCircle, XCircle, AlertCircle, ChefHat, Sofa, Palette,
} from 'lucide-react';

// ─── Canonical category config ─────────────────────────────────────────────────
const CATEGORIES = [
  { key: 'core_attributes',     label: 'Core Attributes', max: 40, icon: Home,         color: '#6366F1' },
  { key: 'feature_flags',       label: 'Features',        max: 8,  icon: Star,         color: '#F59E0B' },
  { key: 'amenities',           label: 'Amenities',       max: 15, icon: Leaf,         color: '#10B981' },
  { key: 'style',               label: 'Style',           max: 10, icon: Palette,      color: '#EC4899' },
  { key: 'kitchen',             label: 'Kitchen',         max: 10, icon: ChefHat,      color: '#F97316' },
  { key: 'furnishing',          label: 'Move-In Ready',   max: 5,  icon: Sofa,         color: '#8B5CF6' },
  { key: 'financial_attributes', label: 'Financial',      max: 3,  icon: DollarSign,   color: '#0891B2' },
  { key: 'lifestyle_tags',      label: 'Lifestyle',       max: 2,  icon: Heart,        color: '#DB2777' },
  { key: 'land_attributes',     label: 'Land & Acreage',  max: 1,  icon: Leaf,         color: '#16A34A' },
  { key: 'utilities',           label: 'Utilities',       max: 1,  icon: Zap,          color: '#0284C7' },
  { key: 'behavioral_signals',  label: 'Engagement',      max: 5,  icon: TrendingUp,   color: '#0A192F' },
];

function ScoreBar({ label, icon: Icon, color, earned, max, delay = 0 }) {
  const hasDefined = max > 0;
  const pct = hasDefined ? Math.round((earned / max) * 100) : 0;
  const isSkipped = !hasDefined || (earned === 0 && max === 0);

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-xs font-semibold"
          style={{ color: isSkipped ? '#94A3B8' : '#374151' }}>
          <Icon size={12} style={{ color: isSkipped ? '#CBD5E1' : color }} />
          {label}
        </span>
        <span className="text-xs font-black tabular-nums"
          style={{ color: isSkipped ? '#CBD5E1' : color }}>
          {isSkipped ? '—' : `${earned}/${max}`}
        </span>
      </div>
      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: '#F1F5F9' }}>
        {!isSkipped && (
          <motion.div className="h-full rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.55, delay: 0.1 + delay, ease: 'easeOut' }}
            style={{ background: color }} />
        )}
        {isSkipped && (
          <div className="h-full w-0 rounded-full" style={{ background: '#E2E8F0' }} />
        )}
      </div>
    </div>
  );
}

function Tag({ text, variant }) {
  const styles = {
    green:  { bg: '#F0FDF4', text: '#16A34A', border: '#BBF7D0', Icon: CheckCircle },
    amber:  { bg: '#FFFBEB', text: '#D97706', border: '#FDE68A', Icon: AlertCircle },
    red:    { bg: '#FEF2F2', text: '#DC2626', border: '#FECACA', Icon: XCircle },
  };
  const s = styles[variant] || styles.green;
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border"
      style={{ background: s.bg, color: s.text, borderColor: s.border }}>
      <s.Icon size={10} />
      {text}
    </span>
  );
}

export default function MatchScoreModal({ listing, onClose }) {
  if (!listing) return null;

  const {
    matchScore = 0,
    matchBreakdown = {},
    tags = [],
    dealbreakers = [],
    address,
    city,
    state,
    price,
    match_reasons = [],
    match_conflicts = [],
  } = listing;

  const scoreColor = matchScore >= 80 ? '#10B981' : matchScore >= 60 ? '#F59E0B' : '#94A3B8';
  const scoreLabel = matchScore >= 80 ? 'Strong Match' : matchScore >= 70 ? 'Good Match' : matchScore >= 50 ? 'Partial Match' : 'Low Match';
  const scoreSub = matchScore >= 80
    ? 'This home closely fits your profile'
    : matchScore >= 60
      ? 'Meets several of your key criteria'
      : 'Some criteria differ from your preferences';

  // Build display rows from breakdown (canonical v2) or estimate from score
  const rows = CATEGORIES.map(cat => {
    const data = matchBreakdown?.[cat.key];
    return {
      ...cat,
      earned: data?.earned ?? (Object.keys(matchBreakdown || {}).length > 0 ? 0 : Math.round(cat.max * matchScore / 100)),
      max:    data?.max    ?? cat.max,
    };
  });

  const hasBreakdown = Object.keys(matchBreakdown || {}).length > 0;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ background: 'rgba(10,25,47,0.75)' }}
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.92, y: 20 }}
          transition={{ type: 'spring', stiffness: 320, damping: 32 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden max-h-[90vh] flex flex-col"
          onClick={e => e.stopPropagation()}
          data-testid="match-score-modal"
        >
          {/* Header */}
          <div className="p-5 pb-4 flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #0A192F 0%, #1a3a5c 100%)' }}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1 min-w-0 pr-3">
                <p className="text-white/40 text-xs font-bold uppercase tracking-widest mb-0.5">Why it fits</p>
                <h2 className="text-base font-black text-white truncate">{address}</h2>
                <p className="text-white/50 text-xs mt-0.5">{city}, {state} · ${price?.toLocaleString()}</p>
              </div>
              <button onClick={onClose} data-testid="close-match-modal"
                className="w-8 h-8 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/20 text-white transition-all flex-shrink-0">
                <X size={15} />
              </button>
            </div>

            {/* Score ring + label */}
            <div className="flex items-center gap-4">
              <div className="relative flex-shrink-0">
                <svg width="72" height="72" viewBox="0 0 72 72">
                  <circle cx="36" cy="36" r="30" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="6" />
                  <motion.circle cx="36" cy="36" r="30"
                    fill="none" stroke={scoreColor} strokeWidth="6" strokeLinecap="round"
                    strokeDasharray={`${2 * Math.PI * 30}`}
                    initial={{ strokeDashoffset: 2 * Math.PI * 30 }}
                    animate={{ strokeDashoffset: 2 * Math.PI * 30 * (1 - matchScore / 100) }}
                    transition={{ duration: 0.9, delay: 0.15, ease: 'easeOut' }}
                    style={{ transformOrigin: '36px 36px', transform: 'rotate(-90deg)' }}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-lg font-black text-white">{matchScore}%</span>
                </div>
              </div>
              <div>
                <div className="text-white font-black text-base">{scoreLabel}</div>
                <div className="text-white/50 text-xs mt-0.5 leading-relaxed">{scoreSub}</div>
              </div>
            </div>
          </div>

          {/* Scrollable body */}
          <div className="overflow-y-auto flex-1">
            <div className="p-5 space-y-5">

              {/* All 7 breakdown rows */}
              <div>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Why this one works for you</p>
                <div className="space-y-3">
                  {rows.map((row, i) => {
                    const { key, ...rowProps } = row;
                    return <ScoreBar key={key} {...rowProps} delay={i * 0.05} />;
                  })}
                </div>
                {!hasBreakdown && (
                  <div className="flex items-start gap-2 p-3 rounded-xl bg-slate-50 border border-slate-100 mt-3">
                    <AlertCircle size={13} className="text-slate-400 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-slate-500 leading-relaxed">
                      Complete your <strong>Buyer Profile</strong> for a personalized breakdown based on your exact preferences.
                    </p>
                  </div>
                )}
              </div>

              {/* Match reasons */}
              {(match_reasons.length > 0 || tags.length > 0) && (
                <div>
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">What Matches</p>
                  <div className="flex flex-wrap gap-1.5">
                    {[...new Set([...match_reasons, ...tags])].map(r => (
                      <Tag key={r} text={r} variant="green" />
                    ))}
                  </div>
                </div>
              )}

              {/* Conflicts */}
              {match_conflicts.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Potential Concerns</p>
                  <div className="flex flex-wrap gap-1.5">
                    {match_conflicts.map(r => (
                      <Tag key={r} text={r} variant="amber" />
                    ))}
                  </div>
                </div>
              )}

              {/* Dealbreakers */}
              {dealbreakers.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Dealbreakers Hit</p>
                  <div className="flex flex-wrap gap-1.5">
                    {dealbreakers.map(r => (
                      <Tag key={r} text={r} variant="red" />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-5 py-4 border-t border-slate-100 flex-shrink-0">
            <button onClick={onClose} data-testid="modal-close-btn"
              className="w-full py-3 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90"
              style={{ background: '#0A192F' }}>
              Got it
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
